import React, { useState, useCallback, useEffect } from "react";
import CustomTable from "src/components/App/AppGrid/CustomTable/CustomTable";
import { Chip, Box, alpha, Typography } from "@mui/material";
import theme from "src/config/theme";
import NoDataFound from "../../NoDataFound/NoDataFound";
import useResponsive from "src/components/hooks/useResponsive";
import { getCasesByPatientId } from "src/modules/app/api/appApi";
import useAppointmentDetails from "../../UseAppointmentDetails";
import { AllTabsMainContainer } from "../Style";
import dayjs from "dayjs";

const columns = [
  { id: "caseNumberCol", label: "Case Number", sx: { width: "200px", minWidth: "150px", textAlign: 'left' } },
  { id: "doctor", label: "Doctor Name", sx: { width: "250px", minWidth: "150px", textAlign: 'left' } },
  { id: "clinic", label: "Clinic Name", sx: { width: "250px", minWidth: "150px", textAlign: 'left' } },
  { id: "openDate", label: "Open Date", sx: { width: "150px", textAlign: 'left' } },
  { id: "validityEndDate", label: "Validity Till", sx: { width: "150px", textAlign: 'left' } },
  {
    id: "status", label: "Status", sx: {
      textAlign: 'center !important',
      "& .css-axw7ok": { justifyContent: 'center' }
    }
  },
];

const CaseHistory = () => {
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const { isMobile, isTablet } = useResponsive();
  const { PatientId } = useAppointmentDetails();
  const [cases, setCases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCases = useCallback(
    (page = 0, perPage = 10) => {
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const params = {
            page: page + 1,
            limit: perPage,
          };
          const response = await getCasesByPatientId({ patientId: PatientId, params });

          if (response?.data && response?.meta) {
            setCases(response.data);
            setTotalCount(response.meta.total);
          } else {
            console.log('Error fetching medical cases');
          }
        } catch (error) {
          console.error("Failed to fetch cases:", error);
        } finally {
          setIsLoading(false);
        }
      }
      fetchData();
    },
    [PatientId],
  );

  useEffect(() => {
    if (PatientId) {
      fetchCases(page, perPage);
    }
  }, [PatientId, fetchCases, page, perPage]);

  const data = cases.map((item) => ({
    id: item.id,
    caseNumberCol: (
      <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
        {item.caseNumber || 'N/A'}
      </Typography>
    ),
    doctor: item.doctor || 'N/A',
    clinic: item.clinic || 'N/A',
    openDate: item.openDate ? dayjs(item.openDate).format("DD-MM-YYYY") : 'N/A',
    validityEndDate: item.validityEndDate ? dayjs(item.validityEndDate).format("DD-MM-YYYY") : 'N/A',
    status: (
      <Chip
        label={item.caseStatus}
        sx={{
          background:
            item.caseStatus === "OPEN" ? "linear-gradient(0deg, rgba(57, 150, 57, 0.18), rgba(57, 150, 57, 0.18))" :
              "linear-gradient(0deg, rgba(150, 57, 57, 0.18), rgba(150, 57, 57, 0.18))",
          color:
            item.caseStatus === "OPEN" ? theme.palette.action.green : theme.palette.action.red,
          ...theme.typography.small,
          borderRadius: '100px',
          padding: '10px 18px 8px 18px',
        }}
      />
    ),
  }));

  return (
    <AllTabsMainContainer>
      <CustomTable
        columns={columns}
        data={data}
        actions={[]}
        page={page}
        disabled={true}
        loading={isLoading}
        onPageChange={(newPage) => {
          setPage(newPage);
        }}
        onPerPageChange={(newPerPage) => {
          setPerPage(newPerPage);
          setPage(0);
        }}
        perPage={perPage}
        totalCount={totalCount}
        noDataComponent={
          <NoDataFound
            title="No Case History Found!"
            buttonText=""
            onClick={() => {}}
          />
        }
      />
    </AllTabsMainContainer>
  );
};

export default CaseHistory;
